// Small indicators: importance bars, sparkline, tiny tile visualisations.
import i18n from '@/i18n';
import { cx, dir as dirOf } from '@/lib/format';
import { rng, smooth, walk, type Pt } from '@/lib/random';

/** Three ascending bars = importance 1…3 (`.sig`). */
export function Importance({ n, light = false }: { n: number; light?: boolean }) {
  return (
    <span className={cx('sig', light && 'light')} aria-label={i18n.t('common:a11y.importance', { n })}>
      {[1, 2, 3].map((i) => <i key={i} className={i <= n ? 'on' : ''} />)}
    </span>
  );
}

/** Three dots = impact level (`.imp3`). */
export function ImpactDots({ n }: { n: number }) {
  return <span className="imp3">{[1, 2, 3].map((i) => <i key={i} className={i <= n ? 'on' : ''} />)}</span>;
}

export function Spark({ seed, trend, change }: { seed: number; trend: number; change?: number }) {
  const ys = walk(seed, 24, trend);
  const pts = ys.map((y, i): Pt => [(i / 23) * 100, 28 - y * 24]);
  return (
    <svg className={cx('spark', change !== undefined && dirOf(change))} viewBox="0 0 100 30" preserveAspectRatio="none" aria-hidden="true">
      <path d={smooth(pts)} />
    </svg>
  );
}

export const VizDots = ({ total, on, hot = -1 }: { total: number; on: number; hot?: number }) => (
  <span className="vz vz-dots">{Array.from({ length: total }, (_, i) => <i key={i} className={i === hot ? 'hot' : i < on ? 'on' : ''} />)}</span>
);

export const VizRing = ({ n, filled }: { n: number; filled: number }) => (
  <span className="vz vz-ring">{Array.from({ length: n }, (_, i) => <i key={i} className={i >= n - filled ? 'on' : ''} />)}</span>
);

export function VizBars({ seed, n = 8 }: { seed: number; n?: number }) {
  const r = rng(seed);
  return (
    <span className="vz vz-bars">
      {Array.from({ length: n }, (_, i) => (
        <i key={i} style={{ '--h': `${i === n - 1 ? 100 : 25 + r() * 60}%` } as React.CSSProperties} className={i === n - 1 ? 'on' : ''} />
      ))}
    </span>
  );
}

export function VizWave({ seed, down = false }: { seed: number; down?: boolean }) {
  const ys = walk(seed, 12, down ? -1.2 : 1.2);
  const pts = ys.map((y, i): Pt => [(i / 11) * 64, 20 - y * 16]);
  return (
    <svg className="vz vz-wave" viewBox="0 0 64 22" aria-hidden="true">
      <path d={smooth(pts)} />
      <circle cx="64" cy={pts[11][1].toFixed(1)} r="2.4" />
    </svg>
  );
}

export function VizScatter({ seed }: { seed: number }) {
  const r = rng(seed);
  return <span className="vz vz-scatter">{Array.from({ length: 16 }, (_, i) => <i key={i} className={r() > 0.72 ? 'on' : ''} />)}</span>;
}

/** The first three alert levels each way (from the API) with the last passed one highlighted. */
export function VizThresholds({ change, levels }: { change: number; levels: number[] }) {
  const lv = levels.slice(0, 3);
  const steps = [...lv.map((l) => -l).reverse(), ...lv];
  const passed = (s: number) => (change < 0 ? s < 0 && change <= s : s > 0 && change >= s);
  const last = steps.filter(passed).sort((a, b) => Math.abs(b) - Math.abs(a))[0];
  return (
    <span className="vz vz-th">
      {steps.map((s) => <i key={s} className={cx(s === last ? 'hot' : passed(s) ? 'on' : '', s === lv[0] && 'gap')} />)}
    </span>
  );
}
